import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function KPIReport() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalNetSales, setTotalNetSales] = useState(0);
    const [totalNumberofPcs, setTotalNumberofPcs] = useState(0);
    const [totalNumberofBills, setTotalNumberofBills] = useState(0);
    const [totalSingleBills, setTotalSingleBills] = useState(0);
    const [totalASP, setTotalASP] = useState(0);
    const [totalUPT, setTotalUPT] = useState(0);
    const [totalVPT, setTotalVPT] = useState(0);
    const [totalPercentageSingleBill, setTotalPercentageSingleBill] = useState(0);
    const [totalNumberofWatches, setTotalNumberofWatches] = useState(0);
    const [totalNumberofJewellery, setTotalNumberofJewellery] = useState(0);
    const [totalASPJewellery, setTotalASPJewellery] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let totalNetSales = 0, totalNumberofPcs = 0, totalNumberofBills = 0;
        let totalSingleBills = 0, totalPercentageSingleBill = 0;
        let totalNumberofWatches = 0, totalNumberofJewellery = 0;
        let totalASPJewellery = 0, totalNetSalesJewellery = 0;

        dataItem.forEach(item => {
            totalNetSales += item.NetSales;
            totalNumberofPcs += item.NumberofPcs;
            totalNumberofBills += item.NumberofBills;
            totalSingleBills += item.NumberofSingleBills;
            totalNumberofWatches += item.NumberofWatches;
            totalNumberofJewellery += item.NumberofJewellery;
            totalASPJewellery += item.ASPJewellery;
            totalNetSalesJewellery += item.NetSalesJewellery;
        });

        setTotalNetSales(totalNetSales);
        setTotalNumberofPcs(totalNumberofPcs);
        setTotalNumberofBills(totalNumberofBills);
        setTotalSingleBills(totalSingleBills);
        setTotalNumberofWatches(totalNumberofWatches);
        setTotalNumberofJewellery(totalNumberofJewellery);

        const totalASP = totalNumberofPcs > 0 ? Math.round(totalNetSales / totalNumberofPcs) : 0;
        const totalUPT = totalNumberofBills > 0 ? (totalNumberofPcs / totalNumberofBills) : 0;
        const totalVPT = totalNumberofBills > 0 ? (totalNetSales / totalNumberofBills) : 0;
        const totalnetjew = totalNumberofJewellery > 0 ? Math.round(totalNetSalesJewellery / totalNumberofJewellery) : 0;
        totalPercentageSingleBill = totalNumberofBills > 0 ? ((totalSingleBills * 100) / totalNumberofBills) : 0;

        setTotalASP(totalASP);
        setTotalUPT(totalUPT);
        setTotalVPT(totalVPT);
        setTotalPercentageSingleBill(totalPercentageSingleBill);
        setTotalASPJewellery(totalnetjew);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/KPIReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching KPI Report Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('KPI Report');

        const rightAlignColumns = [
            'Net Sales', 'Number of Pcs', 'Number of Bills', 'Number of Single Bills',
            'ASP', 'UPT', 'VPT', '% Single Bills', 'Number of Watches', 'ASP Jewellery'
        ];

        worksheet.mergeCells('A1:L1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = `KPI Report From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Store', 'Staff Name', 'Net Sales', 'Number of Pcs', 'Number of Bills',
            'Number of Single Bills', 'ASP', 'UPT', 'VPT', '% Single Bills',
            'Number of Watches', 'ASP Jewellery'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.StoreName, rowData.StaffName,
                parseFloat(rowData.NetSales), parseFloat(rowData.NumberofPcs),
                parseFloat(rowData.NumberofBills), parseFloat(rowData.NumberofSingleBills),
                parseFloat(rowData.ASP), parseFloat(rowData.UPT),
                parseFloat(rowData.VPT), parseFloat(rowData.PercentageSingleBill),
                parseFloat(rowData.NumberofWatches), parseFloat(rowData.ASPJewellery),
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                if (headerName === 'UPT') cell.numFmt = '0.000';
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const totalRowValues = [
            '', 'Total',
            parseFloat(totalNetSales), parseFloat(totalNumberofPcs),
            parseFloat(totalNumberofBills), parseFloat(totalSingleBills),
            parseFloat(totalASP), parseFloat(totalUPT),
            parseFloat(totalVPT.toFixed(0)), parseFloat(totalPercentageSingleBill.toFixed(1)),
            parseFloat(totalNumberofWatches), parseFloat(totalASPJewellery)
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            if (headerName === 'UPT') cell.numFmt = '0.000';
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'KPIReport.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">KPI Report</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Store</th>
                            <th>Staff Name</th>
                            <th style={{ textAlign: 'right' }}>Net Sales</th>
                            <th style={{ textAlign: 'right' }}>Number of Pcs</th>
                            <th style={{ textAlign: 'right' }}>Number of Bills</th>
                            <th style={{ textAlign: 'right' }}>Number of Single Bills</th>
                            <th style={{ textAlign: 'right' }}>ASP</th>
                            <th style={{ textAlign: 'right' }}>UPT</th>
                            <th style={{ textAlign: 'right' }}>VPT</th>
                            <th style={{ textAlign: 'right' }}>% Single Bills</th>
                            <th style={{ textAlign: 'right' }}>Number of Watches</th>
                            <th style={{ textAlign: 'right' }}>ASP Jewellery</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={12} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{data.StoreName}</td>
                                    <td>{data.StaffName}</td>
                                    <td style={{ textAlign: 'right' }}>{data.NetSales}</td>
                                    <td style={{ textAlign: 'right' }}>{data.NumberofPcs}</td>
                                    <td style={{ textAlign: 'right' }}>{data.NumberofBills}</td>
                                    <td style={{ textAlign: 'right' }}>{data.NumberofSingleBills}</td>
                                    <td style={{ textAlign: 'right' }}>{data.ASP}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data.UPT).toFixed(3)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.VPT}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data.PercentageSingleBill).toFixed(1)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.NumberofWatches}</td>
                                    <td style={{ textAlign: 'right' }}>{data.ASPJewellery}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>Total</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalNetSales}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalNumberofPcs}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalNumberofBills}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalSingleBills}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalASP}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{Number(totalUPT).toFixed(3)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{Number(totalVPT).toFixed(0)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{Number(totalPercentageSingleBill).toFixed(1)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalNumberofWatches}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalASPJewellery}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default KPIReport;
